/**
 * `/settings/presses` — the shop's screen-printing presses (docs/screen-print-station-audit-and-plan-v1.md
 * § 3.6). Register each press as stations · print heads · flash stations so a design's screen spec can
 * name the head every colour sits on and the packet can draw the carousel. One press is the default a
 * spec's heads refer to when it names none. Shop configuration, gated exactly like Film rolls.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Factory, Loader2, Pencil, Plus, RefreshCw, Star, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { ApiClient } from '@/lib/api/client';
import { useApiClient } from '@/lib/api/hooks';
import { PressCarousel } from './press-carousel';
import {
  PRESS_MAX_STATIONS,
  type PressSummary,
  parseFlashStations,
  pressFormValid,
  pressShapeLabel,
} from './screen-print-spec-lib';

/** The shop's automatic, offered pre-filled the first time the list is empty (D17). */
const SEED_PRESS = {
  name: 'M&R 12/10',
  kind: 'automatic' as const,
  stations: '12',
  printHeads: '10',
  flashStations: '2, 8',
};

type PressForm = {
  name: string;
  kind: 'automatic' | 'manual';
  stations: string;
  printHeads: string;
  flashStations: string;
};

/** The form's starting values: the press being edited, else the seed on an empty list, else blank. */
function initialForm(press: PressSummary | undefined, seed: boolean): PressForm {
  if (press) {
    return {
      name: press.name,
      kind: press.kind,
      stations: String(press.stations),
      printHeads: String(press.printHeads),
      flashStations: press.flashStations.join(', '),
    };
  }
  return seed
    ? SEED_PRESS
    : { name: '', kind: 'automatic', stations: '', printHeads: '', flashStations: '' };
}

export function usePresses() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['presses'],
    queryFn: async () => {
      const { data, error } = await client.GET('/presses');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load presses.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.items as PressSummary[];
    },
  });
}

export function PressesPage() {
  const query = usePresses();
  const presses = query.data ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Presses</h1>
          <p className="text-sm text-muted-foreground">
            The screen-printing presses in the shop, as stations, print heads and flash stations. A
            design's screen spec names the head each colour sits on; the default press is the one
            those head numbers refer to when the spec names none.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={query.isFetching ? 'size-4 animate-spin' : 'size-4'} />
            Refresh
          </Button>
          <PressDialog mode="create" seed={query.isSuccess && presses.length === 0} />
        </div>
      </header>

      {query.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load presses</AlertTitle>
          <AlertDescription>
            {query.error instanceof Error ? query.error.message : 'Network error.'}
          </AlertDescription>
        </Alert>
      )}

      {query.isPending && <Skeleton className="h-40 w-full" />}

      {query.isSuccess &&
        (presses.length === 0 ? <EmptyState /> : <PressTable presses={presses} />)}
    </div>
  );
}

function PressTable({ presses }: { presses: PressSummary[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[560px] text-sm">
        <thead className="bg-muted/40">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Press</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Kind</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              Layout
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground sr-only">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {presses.map((press) => (
            <tr key={press.pressId} className="border-t [&:nth-child(even)]:bg-muted/20">
              <td className="px-3 py-2 align-top">
                <div className="flex items-center gap-2">
                  <Factory className="size-4 shrink-0 text-muted-foreground" />
                  <span className="font-medium">{press.name}</span>
                  {press.isDefault && (
                    <Badge variant="outline" className="border-amber-300 text-amber-700">
                      <Star className="size-3" /> Default
                    </Badge>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 align-top capitalize">{press.kind}</td>
              <td className="px-3 py-2 align-top tabular-nums">{pressShapeLabel(press)}</td>
              <td className="px-3 py-2 text-right align-top">
                <PressRowActions press={press} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PressRowActions({ press }: { press: PressSummary }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<null | 'edit' | 'delete'>(null);

  const setDefault = useMutation({
    mutationFn: async () => {
      const res = await client.POST('/presses/{pressId}/default', {
        params: { path: { pressId: press.pressId } },
      });
      if (res.error || !res.data?.ok) throw new Error(res.error?.error?.detail ?? 'Failed.');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['presses'] }),
  });

  return (
    <div className="flex items-center justify-end gap-1">
      {!press.isDefault && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDefault.mutate()}
          disabled={setDefault.isPending}
          title="Make default"
        >
          {setDefault.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Star className="size-4" />
          )}
          Default
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setDialog('edit')}
        aria-label={`Edit ${press.name}`}
      >
        <Pencil className="size-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="text-destructive"
        onClick={() => setDialog('delete')}
        aria-label={`Delete ${press.name}`}
      >
        <Trash2 className="size-4" />
      </Button>
      <PressDialog
        mode="edit"
        press={press}
        open={dialog === 'edit'}
        onClose={() => setDialog(null)}
      />
      <DeletePressDialog press={press} open={dialog === 'delete'} onClose={() => setDialog(null)} />
    </div>
  );
}

type PressBody = {
  name: string;
  kind: 'automatic' | 'manual';
  stations: number;
  printHeads: number;
  flashStations: number[];
  isDefault?: boolean;
};

async function savePress(client: ApiClient, body: PressBody, pressId?: string): Promise<void> {
  const res = pressId
    ? await client.PATCH('/presses/{pressId}', { params: { path: { pressId } }, body })
    : await client.POST('/presses', { body });
  if (res.error || !res.data?.ok) {
    throw new Error(res.error?.error?.detail ?? 'Could not save the press.');
  }
}

function PressDialog({
  mode,
  press,
  seed = false,
  open: controlledOpen,
  onClose,
}: {
  mode: 'create' | 'edit';
  press?: PressSummary;
  /** Pre-fill the shop's M&R the first time — the list is empty and this is the press we know. */
  seed?: boolean;
  open?: boolean;
  onClose?: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [selfOpen, setSelfOpen] = useState(false);
  const open = mode === 'create' ? selfOpen : (controlledOpen ?? false);
  const close = () => (mode === 'create' ? setSelfOpen(false) : onClose?.());

  const [form, setForm] = useState(() => initialForm(press, seed));
  const [makeDefault, setMakeDefault] = useState(press?.isDefault ?? seed);
  useEffect(() => {
    if (open) {
      setForm(initialForm(press, seed));
      setMakeDefault(press?.isDefault ?? seed);
    }
  }, [open, press, seed]);

  const flash = parseFlashStations(form.flashStations);
  const stations = Number(form.stations);
  const printHeads = Number(form.printHeads);
  const valid = pressFormValid({ name: form.name, stations, printHeads, flashStations: flash });
  const physicsBroken =
    flash !== null &&
    Number.isInteger(stations) &&
    Number.isInteger(printHeads) &&
    stations > 0 &&
    printHeads > 0 &&
    !pressFormValid({ name: form.name || 'x', stations, printHeads, flashStations: flash });

  const mutation = useMutation({
    mutationFn: async () => {
      if (!valid || flash === null) throw new Error('Check the press layout.');
      await savePress(
        client,
        {
          name: form.name.trim(),
          kind: form.kind,
          stations,
          printHeads,
          flashStations: flash,
          isDefault: makeDefault,
        },
        press?.pressId,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['presses'] });
      close();
    },
  });

  const previewable = valid && flash !== null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (mode === 'create') setSelfOpen(o);
        if (!o) {
          mutation.reset();
          onClose?.();
        }
      }}
    >
      {mode === 'create' && (
        <Button size="sm" onClick={() => setSelfOpen(true)}>
          <Plus className="size-4" />
          New press
        </Button>
      )}
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Register a press' : 'Edit press'}</DialogTitle>
          <DialogDescription>
            Stations around the carousel, how many hold a print head, and which hold a flash unit.
            The ring redraws as you type.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_220px]">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="press-name">Name</Label>
              <Input
                id="press-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="M&R 12/10"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Kind</Label>
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={form.kind}
                onValueChange={(v) => {
                  if (v === 'automatic' || v === 'manual') setForm({ ...form, kind: v });
                }}
              >
                <ToggleGroupItem value="automatic">Automatic</ToggleGroupItem>
                <ToggleGroupItem value="manual">Manual</ToggleGroupItem>
              </ToggleGroup>
              <p className="text-xs text-muted-foreground">
                On an automatic, head order is print order and the flashes sit on fixed stations. A
                manual has no fixed flashes — the spec's “flash after” does that job.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="press-stations">Stations</Label>
                <Input
                  id="press-stations"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  max={PRESS_MAX_STATIONS}
                  value={form.stations}
                  onChange={(e) => setForm({ ...form, stations: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="press-heads">Print heads</Label>
                <Input
                  id="press-heads"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  max={PRESS_MAX_STATIONS}
                  value={form.printHeads}
                  onChange={(e) => setForm({ ...form, printHeads: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="press-flash">Flash stations</Label>
                <Input
                  id="press-flash"
                  value={form.flashStations}
                  onChange={(e) => setForm({ ...form, flashStations: e.target.value })}
                  placeholder="2, 8"
                  aria-invalid={flash === null}
                />
              </div>
            </div>
            {flash === null && (
              <p className="text-sm text-destructive">
                Flash stations are whole numbers separated by commas — “2, 8”.
              </p>
            )}
            {physicsBroken && (
              <p className="text-sm text-destructive">
                Heads plus flash units cannot exceed the stations, and each flash station must be a
                distinct station on the press.
              </p>
            )}
            <label className="flex flex-wrap items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={makeDefault}
                // Promote-only, like the roll default: the press that IS the default stays checked.
                disabled={mode === 'edit' && press?.isDefault === true}
                onChange={(e) => setMakeDefault(e.target.checked)}
              />
              Default press — the one a spec's head numbers refer to
              {mode === 'edit' && press?.isDefault === true && (
                <span className="text-xs text-muted-foreground">
                  — already the default; promote another press to change it
                </span>
              )}
            </label>
            {mutation.isError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {mutation.error instanceof Error ? mutation.error.message : 'Save failed.'}
                </AlertDescription>
              </Alert>
            )}
          </div>
          <div className="flex items-start justify-center">
            {previewable ? (
              <PressCarousel
                press={{ kind: form.kind, stations, printHeads, flashStations: flash }}
                screens={[]}
                caption={[form.name.trim() || 'press', `${printHeads} heads`]}
                className="w-full max-w-[220px]"
              />
            ) : (
              <p className="pt-8 text-center text-xs text-muted-foreground">
                The ring draws once the layout is valid.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => mutation.mutate()} disabled={!valid || mutation.isPending}>
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {mode === 'create' ? 'Register press' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeletePressDialog({
  press,
  open,
  onClose,
}: {
  press: PressSummary;
  open: boolean;
  onClose: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      const res = await client.DELETE('/presses/{pressId}', {
        params: { path: { pressId: press.pressId } },
      });
      if (res.error || !res.data?.ok) throw new Error(res.error?.error?.detail ?? 'Delete failed.');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['presses'] });
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          mutation.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete “{press.name}”?</DialogTitle>
          <DialogDescription>
            Removes it from the press list. Design specs that named its heads keep their numbers;
            the default press cannot be deleted — promote another first.
          </DialogDescription>
        </DialogHeader>
        {mutation.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {mutation.error instanceof Error ? mutation.error.message : 'Delete failed.'}
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Check className="size-4" />
            )}
            Delete press
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState() {
  return (
    <div className="rounded-lg border border-dashed p-8 text-center">
      <Factory className="mx-auto size-6 text-muted-foreground" />
      <p className="mt-2 text-sm font-medium">No presses yet</p>
      <p className="mt-1 text-sm text-muted-foreground">
        Register the shop's automatic — New press comes pre-filled with the M&R 12/10 (12 stations,
        10 heads, flashes on 2 and 8). Correct it if the flashes sit elsewhere.
      </p>
    </div>
  );
}
